import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ENUM_ORDER_PROGRESS, ENUM_ORDER_STATUS } from 'utils/enum';
import { ReactComponent as NoteIcon } from 'assets/icons/note.svg';
import { getOrderMealsCount, getOrderStatus } from 'utils/functions';
import { Button } from '@cokitchen/cokitchen-components';
import ComplaintModal from 'components/modals/complaint-log-modal';
import BulkOrderMealAccordion from 'components/meal-accordion/bulk-order-meal-accordion';
import GroupOrderMealAccordion from 'components/meal-accordion/group-order-meal-accordion';
import { LocationAltIcon } from 'cokitchen-icons';
import bike from 'assets/images/bike.png';
import singlePackage from 'assets/images/package.png';
import { ReactComponent as PhoneIcon } from 'assets/icons/phone-filled.svg';
import { ReactComponent as WhatsAppIcon } from 'assets/icons/whatsapp.svg';
import { ReactComponent as WavingFlagIcon } from 'assets/icons/waving-flag.svg';
import OrderInTransit from './order-in-transit';
import db from 'utils/firebase-config';
import PaymentReceived from './payment-received';
import OrderStatusProgress from 'components/order-status-progress';
import EditOrderDetails from 'pages/orders/edit-order-details';
import TrackOrder from 'components/track-order';
import ReportRiderModal from 'components/modals/report-rider-modal';
import { Bars } from 'react-loader-spinner';
import { CoKitchenType } from 'models/cokitchen.model';
import { apiService } from 'services/base.service';
import { OrderType } from 'models/order.model';
import { doc, onSnapshot } from 'firebase/firestore';

type Props = {
  order: OrderType;
};

const tagColorTokens: Record<string, { wrapper: string; dot: string }> = {
  [ENUM_ORDER_STATUS.PENDING]: {
    wrapper: 'bg-gray-16 text-gray-8',
    dot: 'bg-gray-8'
  },
  [ENUM_ORDER_STATUS.SCHEDULED]: {
    wrapper: 'bg-[#E5F4FF] text-[#0080E5]',
    dot: 'bg-[#0080E5]'
  },
  [ENUM_ORDER_STATUS.ONGOING]: {
    wrapper: 'bg-[#FFF2E0] text-[#FF9500]',
    dot: 'bg-[#FF9500]'
  }
};

const OngoingOrder = (props: Props) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { order } = props;
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [firestoreOrder, setFirestoreOrder] = useState<OrderType>();
  const [showReportRiderModal, setShowReportRiderModal] = useState(false);
  const totalMeals = getOrderMealsCount(order);
  const totalBrands = order?.calculated_order?.brand_ids?.length || 0;
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [cokitchen, setCokitchen] = useState<CoKitchenType>();
  const orderStatus = getOrderStatus(order);

  const orderProgressStep = useMemo(() => {
    if (!firestoreOrder) return null;
    const {
      paid,
      kitchen_prepared,
      kitchen_accepted,
      rider_assigned,
      completed,
      calculated_order
    } = firestoreOrder;
    if (completed) return ENUM_ORDER_PROGRESS.ORDER_COMPLETED;
    else if (!calculated_order.pickup && kitchen_prepared && rider_assigned)
      return ENUM_ORDER_PROGRESS.ORDER_IN_TRANSIT;
    else if (kitchen_prepared && !rider_assigned) return ENUM_ORDER_PROGRESS.AWAITING_RIDER;
    else if (kitchen_accepted && !kitchen_prepared) return ENUM_ORDER_PROGRESS.PREPARING_YOUR_ORDER;
    else if (paid) return ENUM_ORDER_PROGRESS.PAYMENT_RECEIVED;
    return ENUM_ORDER_PROGRESS.PAYMENT_RECEIVED;
  }, [firestoreOrder]);

  const getFirestoreOrder = async () => {
    try {
      onSnapshot(doc(db, 'scheduled_orders', order.id), (docSnap) => {
        if (docSnap.exists() && docSnap.data()) {
          setFirestoreOrder(docSnap.data() as OrderType);
        } else {
          onSnapshot(doc(db, 'tracking_orders', order.id), (trackingDocSnap) => {
            if (trackingDocSnap.exists() && trackingDocSnap.data()) {
              setFirestoreOrder(trackingDocSnap.data() as OrderType);
            } else {
              setFirestoreOrder(order);
            }
          });
        }
      });
    } catch (error) {
      console.error('Error getting document:', error);
    }
  };

  const getCokitchen = async () => {
    const res: any = await apiService.post(`internal/location`, {
      baseURL: process.env.REACT_APP_ORDER_API_URL,
      isInternal: true,
      data: {
        lat: Number(order.calculated_order.lat),
        lng: Number(order.calculated_order.lng)
      }
    });
    setCokitchen(res.data.cokitchen);
  };

  const openTimelineModal = () => {
    setShowTimelineModal(!showTimelineModal);
  };

  useEffect(() => {
    if (order) {
      getFirestoreOrder();
      getCokitchen();
    }
  }, [order]);

  if (!orderProgressStep || !cokitchen)
    return (
      <div className='grow w-full h-screen flex justify-center pt-[25vh]'>
        <Bars color='#E60A2B' height={50} width={50} />
      </div>
    );

  const sideContent = {
    [ENUM_ORDER_PROGRESS.PAYMENT_RECEIVED]: (
      <PaymentReceived order={order} step={orderProgressStep} onViewTimeline={openTimelineModal} />
    ),
    [ENUM_ORDER_PROGRESS.PREPARING_YOUR_ORDER]: (
      <div className='p-[6px] rounded-[10px] bg-[#F8F7F7]'>
        <OrderStatusProgress
          order={order}
          step={orderProgressStep}
          onViewTimeline={openTimelineModal}
        />
      </div>
    ),
    [ENUM_ORDER_PROGRESS.AWAITING_RIDER]: (
      <div className='p-[6px] rounded-[10px] bg-[#F8F7F7]'>
        <OrderStatusProgress
          order={order}
          step={orderProgressStep}
          onViewTimeline={openTimelineModal}
        />
      </div>
    ),
    [ENUM_ORDER_PROGRESS.ORDER_IN_TRANSIT]: (
      <OrderInTransit step={orderProgressStep} order={order} onViewTimeline={openTimelineModal} />
    ),
    [ENUM_ORDER_PROGRESS.READY_FOR_PICKUP]: (
      <OrderInTransit step={orderProgressStep} order={order} onViewTimeline={openTimelineModal} />
    ),
    [ENUM_ORDER_PROGRESS.ORDER_COMPLETED]: (
      <div className='p-[6px] rounded-[10px] bg-[#F8F7F7]'>
        <OrderStatusProgress
          order={order}
          step={orderProgressStep}
          onViewTimeline={openTimelineModal}
        />
      </div>
    )
  };

  if (searchParams.get('edit') === 'true') return <EditOrderDetails order={order} />;
  return (
    <div className='flex flex-col gap-10'>
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <div className='flex items-center gap-2'>
          <p className='text-gray-10 text-2xl font-semibold'>Order #{id}</p>
          <div
            className={`h-[22px] flex items-center justify-center gap-1 px-2 rounded-[40px] ${tagColorTokens[orderStatus].wrapper}`}>
            <div className={`w-2 h-2 rounded-full ${tagColorTokens[orderStatus].dot}`} />
            <p className='text-xs font-medium capitalize'>{orderStatus}</p>
          </div>
        </div>
        {orderStatus === ENUM_ORDER_STATUS.SCHEDULED ? (
          <Button
            disabled={order?.kitchen_confirmed}
            variant='block'
            color='primary'
            className='w-fit h-[40px]'
            onClick={() => {
              setSearchParams({ edit: 'true' });
            }}>
            Edit Order
          </Button>
        ) : null}
      </div>

      <div className='relative flex flex-col md:flex-row items-start gap-[52px]'>
        <div className='block md:sticky top-5 w-full md:w-[50%]'>
          {sideContent[orderProgressStep]}
        </div>

        <div className='w-full md:w-[50%] flex flex-col gap-[30px]'>
          <p className='text-xl font-semibold'>Delivery Details</p>
          <div className='flex flex-col gap-5'>
            {orderStatus === ENUM_ORDER_STATUS.ONGOING && order.calculated_order.pickup ? (
              <div className='flex items-center gap-3 pb-5 border-b-4 border-[#F7F7F7]'>
                <img src={singlePackage} className='w-9 h-9' />
                <article>
                  <p className='font-medium'>Pickup order</p>
                  <p className='text-sm text-[#96999C] truncate'>{cokitchen.address}</p>
                </article>
                <div className='flex items-center gap-3 ml-auto'>
                  {cokitchen?.frontdesk_phone_number?.[0] ? (
                    <a
                      href={`tel:${cokitchen.frontdesk_phone_number[0]}`}
                      className='w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F4F4]'>
                      <PhoneIcon />
                    </a>
                  ) : null}
                  {cokitchen?.frontdesk_whatsapp?.[0] ? (
                    <a
                      href={`https://wa.me/${cokitchen.frontdesk_whatsapp[0]}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F4F4]'>
                      <WhatsAppIcon />
                    </a>
                  ) : null}
                </div>
              </div>
            ) : orderStatus === ENUM_ORDER_STATUS.ONGOING && !order.calculated_order.pickup ? (
              <div className='flex items-center gap-3 pb-5 border-b-4 border-[#F7F7F7]'>
                <img src={bike} className='w-9 h-9' />
                <article>
                  <p className='font-medium'>
                    {order?.rider
                      ? `${order?.rider?.first_name} ${order?.rider?.last_name}`
                      : 'No rider assigned yet'}
                  </p>
                  <p className='text-sm text-[#96999C]'>Dispatch</p>
                </article>
                <div className='flex items-center gap-3 ml-auto'>
                  {order?.rider?.phone_number ? (
                    <a
                      href={`tel:${order.user.phone_number}`}
                      className='w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F4F4]'>
                      <PhoneIcon />
                    </a>
                  ) : null}
                  {order?.rider?.phone_number ? (
                    <a
                      href={`https://wa.me/${order.rider.phone_number}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F4F4]'>
                      <WhatsAppIcon />
                    </a>
                  ) : null}
                  {order?.rider ? (
                    <button
                      className='w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F4F4]'
                      onClick={() => setShowReportRiderModal(true)}>
                      <WavingFlagIcon />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className='flex items-center gap-3 [&>svg]:shrink-0'>
              <LocationAltIcon color='#96999C' />
              <p className='truncate'>{order.calculated_order.address_details.address_line}</p>
            </div>
            <div className='flex items-center gap-3 [&>svg]:shrink-0'>
              <NoteIcon />
              <p
                className={`${
                  !order.order_details?.rider_note ? 'text-[#A3A3A3] cursor-not-allowed' : ''
                }`}>
                {order.order_details?.rider_note || 'No delivery instructions'}
              </p>
            </div>
          </div>

          <div className='flex flex-col gap-5 pt-6 pb-2 border border-[#EAEBEB] rounded-[10px]'>
            <div className='px-[18px]'>
              <p className='text-xl font-semibold'>Order details</p>
              <p className='text-sm text-[#6F7376]'>
                {totalMeals}&nbsp;{totalMeals === 1 ? 'meal' : 'meals'}, {totalBrands}&nbsp;
                {totalBrands === 1 ? 'brand' : 'brands'}
              </p>
            </div>
            <div className='w-full flex flex-col space-y-4'>
              {order.calculated_order.is_company_group_order &&
              order.calculated_order.is_group_order ? (
                <>
                  <div className='mb-2'>
                    {order.calculated_order?.meals.map((meal, index) => (
                      <BulkOrderMealAccordion meal={meal} key={index} />
                    ))}
                  </div>
                  {order.calculated_order.sub_calculated_order_ids?.map((meal, index) => (
                    <GroupOrderMealAccordion meal={meal} key={index} />
                  ))}
                </>
              ) : (
                order.calculated_order?.meals.map((meal, index) => (
                  <BulkOrderMealAccordion meal={meal} key={index} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <TrackOrder
        order={order}
        showModal={showTimelineModal}
        title={orderProgressStep}
        onClose={openTimelineModal}
      />
      <ComplaintModal show={showComplaintModal} onClose={() => setShowComplaintModal(false)} />
      <ReportRiderModal
        show={showReportRiderModal}
        onClose={() => setShowReportRiderModal(false)}
      />
    </div>
  );
};

export default OngoingOrder;
